import type { ApolloClient } from "@apollo/client";
import { getDebugParts } from "~utils";
import { ConfiguratorContext_LoadConfigDocument } from "./Configurator.context.generated";

/**
 * After save, logs when Apollo cannot satisfy `ConfiguratorContext_LoadConfig` from cache alone
 * (`diff.complete` false / `missing` set). Usually triggers a silent follow-up LoadConfig request.
 *
 * Dev-only, no-op in test/production.
 */
export function logLoadConfigCacheDiffAfterSave(client: ApolloClient<unknown>, ccfgId: string | undefined): void {
  if (process.env.NODE_ENV !== "development" || !ccfgId) return;

  const diff = client.cache.diff({
    query: ConfiguratorContext_LoadConfigDocument,
    variables: { id: ccfgId, debugParts: getDebugParts() },
    returnPartialData: true,
    optimistic: false,
  });

  const hasGap = !diff.complete || diff.missing != null;
  if (!hasGap) return;

  console.error(
    `WARNING: After save, the Apollo cache no longer has everything ConfiguratorContext_LoadConfig needs.
The app will usually fire another LoadConfig request (cache-first + incomplete read) — easy to miss.

Typical cause: saveCheckoutConfig wrote back a smaller field set than LoadConfig still expects for the
same normalized records (merge replaces nested objects). Compare fragments: widen
\`ConfiguratorContextSaveCheckoutConfig_CheckoutConfig\`, or stop selecting those fields on Load if you
do not need them after save.

Details (complete / missing) are in the object below.`,
    { ccfgId, complete: diff.complete, missing: diff.missing },
  );
}
